// pages/PlansPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  fetchPackages,
  selectPackages,
  selectSubscriptionLoading,
  // selectSubscriptionError,
  selectPackagesError,
} from "../../slices/subscriptionSlice";
import SubscriptionModal from "../../components/SubscriptionModal";
import { selectIsActive } from "../../slices/subscriptionSlice";
import PackageCard from "../../components/PackageCard";
import type { SubscriptionPackage } from "../../types/subscription";


interface PlansPageProps {
  onSubscriptionComplete?: () => void;
}

export default function PlansPage({ onSubscriptionComplete }: PlansPageProps = {}) {
  const dispatch = useAppDispatch();
  const packages = useAppSelector(selectPackages);
  const loading = useAppSelector(selectSubscriptionLoading);
  const packagesError = useAppSelector(selectPackagesError);
  const subStatus = useAppSelector(selectIsActive); // "none"|"trial"|"active"|"expired"

  // const subscription = dispatch(fetchSubscription(null))
  // console.log(subscription);

  // console.log("Sub status: ", subStatus);
  
  const [selectedPkg, setSelectedPkg] = useState<SubscriptionPackage | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  useEffect(() => {
    dispatch(fetchPackages(null));
  }, [dispatch]);

  const isFirstTimer = subStatus === false;

  const openModal = (pkg: any) => {
    setSelectedPkg(pkg);
    setModalOpen(true);
  };

  // Best-value calculation (simple heuristic: highest employee_count_limit or unlimited)
  const bestPackageId = useMemo(() => {
    if (!packages?.length) return null;
    let best = packages[0];
    let bestLimit = -1;
    packages.forEach((p: SubscriptionPackage) => {
      const details = p.PackageDetails?.[0];
      const val = details?.monthlySalesLimit === null ? Number.POSITIVE_INFINITY : Number(details?.employeeLimit ?? 0);
      if (val > bestLimit) {
        bestLimit = val;
        best = p;
      }
    });
    return best?.id ?? null;
    // return packages?.find(pkg => pkg.name == "Ultimate")?.id;
  }, [packages]);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-2xl font-semibold mb-4 text-primary">Choose a Pricing plan that works for you</h2>
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-lg border p-6">
              <div className="mb-4 h-6 w-3/5 bg-gray-200" />
              <div className="h-32 bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (packagesError) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-2xl font-semibold mb-4 text-primary">Choose a Pricing plan that works for you</h2>
        <h3 className="text-xl font-semibold mb-4 text-primary">Storepilot gives you a range of great deals based on your business needs</h3>
        <div className="rounded-lg border p-6">
          <p className="mb-4 text-red-600">Failed to load plans: {packagesError}</p>
          <button
            onClick={() => dispatch(fetchPackages(null))}
            className="rounded bg-primary px-4 py-2 text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-center">
      <h2 className="text-2xl font-semibold mb-4">Choose a Pricing plan that works for you</h2>
      <h3 className="text-xl font-semibold mb-4 text-primary">Storepilot gives you a range of great deals based on your business needs</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:px-8 py-8">
        {packages?.map((pkg: any, i) => {
          const isBest = pkg.id === bestPackageId;
          return (
            <PackageCard
                  key={i}
                  pkg={pkg}
                  isBest={isBest}
                  onSelect={(p) => {
                    openModal(p)
                  }}
                />
              );
            })}
      </div>

      {selectedPkg && (
        <SubscriptionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          pkg={selectedPkg}
          isFirstTimer={isFirstTimer}
          onSubscriptionSuccess={onSubscriptionComplete}
        />
      )}
    </div>
  );
}
